import { z } from "zod";
import { workflowCheckpointGenerationSchema } from "./WorkflowApproval";

export const ApprovalReviewTraceSchema = z.object({
  tenantId:z.string().min(1),
  id: z.string().min(1),
  workflowId: z.string().nullable(),
  nodeId: z.string().nullable(),
  status: z.enum([
    "SUCCESS",
    "FAILED",
    "IN_PROGRESS",
    "SUSPENDED",
    "QUARANTINED",
  ]),
  checkpointId: workflowCheckpointGenerationSchema,
  startedAt: z.string().datetime().nullable(),
});
export const ApprovalReviewDocumentSchema = z.object({
  number: z.string().nullable(),
  reference: z.string().nullable(),
  issueDate: z.string().nullable(),
  seller: z.string().nullable(),
  buyer: z.string().nullable(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullable(),
  amount: z.number().finite().nullable(),
  lines: z.array(
    z.object({
      description: z.string().nullable(),
      quantity: z.number().finite().nullable(),
      netAmount: z.number().finite().nullable(),
    }),
  ),
});
export const WorkflowDocumentReviewSchema=z.discriminatedUnion('state',[
  z.object({state:z.literal('NONE')}),
  z.object({state:z.literal('UNAVAILABLE'),documentId:z.string().nullable()}),
  z.object({state:z.literal('SAVED'),documentId:z.string().min(1),revision:z.string().regex(/^review-[a-f0-9]{64}$/),updatedAt:z.string().datetime(),status:z.string(),artifacts:z.array(z.object({id:z.string(),kind:z.string(),status:z.string(),label:z.string(),fileName:z.string().nullable()}))}),
]);
export const WorkflowApprovalReviewSchema = z.object({
  documentReview:WorkflowDocumentReviewSchema.default({state:'UNAVAILABLE',documentId:null}),
  trace: ApprovalReviewTraceSchema,
  snapshot: z.object({
    state: z.enum(["VERIFIED", "LEGACY", "UNAVAILABLE", "INACTIVE"]),
    recordedAt: z.string().datetime().nullable(),
    definitionHash: z.string().nullable(),
    nodeType: z.string().nullable(),
  }),
  document: ApprovalReviewDocumentSchema.nullable(),
  source: z
    .object({
      documentId: z.string().min(1),
      storageKey: z.string().nullable(),
    })
    .nullable(),
  rules: z
    .array(
      z.object({
        field: z.string(),
        operator: z.string(),
        value: z.string().nullable(),
      }),
    )
    .nullable(),
  validation: z.object({
    status: z.enum(["VALID", "INVALID", "PENDING", "UNKNOWN"]),
    issues: z.array(
      z.object({ path: z.string().nullable(), message: z.string() }),
    ),
  }),
  permissions: z.object({ approve: z.boolean(), reject: z.boolean() }),
  blockers: z.array(
    z.enum(["ROLE", "INACTIVE", "CHECKPOINT", "VALIDATION", "DEFINITION", "SOURCE", "DOCUMENT_STATE"]),
  ),
  history: z.array(
    z.object({
      nodeId: z.string(),
      status: z.string(),
      at: z.string().datetime().nullable(),
    }),
  ),
});
export type WorkflowApprovalReview = z.infer<
  typeof WorkflowApprovalReviewSchema
>;

export const WorkflowReviewInboxQuerySchema = z.object({
  limit: z.number().int().min(1).max(100).default(25),
  offset: z.number().int().min(0).max(100000).default(0),
  q: z.string().trim().max(200).default(""),
  reason: z.enum(["all", "validation", "duplicate", "unknown"]).default("all"),
});
export const WorkflowReviewInboxPageSchema = z.object({
  data: z.array(
    z.object({
      id: z.string().min(1),
      workflowId: z.string().nullable(),
      nodeId: z.string().nullable(),
      reason: z.string().nullable(),
      startedAt: z.string().datetime().nullable(),
    }),
  ),
  pagination: z.object({
    limit: z.number().int().min(1).max(100),
    offset: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
  }),
});
export type WorkflowReviewInboxQuery = z.infer<
  typeof WorkflowReviewInboxQuerySchema
>;
export type WorkflowReviewInboxPage = z.infer<
  typeof WorkflowReviewInboxPageSchema
>;
